import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Clock, Globe } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { CONTACT, WHATSAPP_LINK } from "../../data/flyzone";
import { useBooking } from "./BookingProvider";

const items = [
  { icon: Phone, label: "Call Us", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}`, testid: "contact-phone" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: WHATSAPP_LINK, testid: "contact-whatsapp" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  { icon: Globe, label: "Website", value: CONTACT.website, href: CONTACT.websiteUrl, testid: "contact-website" },
  { icon: MapPin, label: "Visit Us", value: CONTACT.address, href: "#map", testid: "contact-address" },
];

export const Contact = () => {
  const { API } = useBooking();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will reach out soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-28 bg-[#0D0D0D] border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Get In Touch</span>
          <h2 className="mt-3 text-3xl sm:text-5xl font-display font-bold tracking-tight">
            Let's get you on the road
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: info + map */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((it) => (
                <a
                  key={it.label}
                  href={it.href}
                  data-testid={it.testid}
                  target={it.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="group flex items-start gap-3 rounded-2xl p-5 bg-[#141414] border border-white/10 hover:border-primary/40 transition-colors"
                >
                  <div className="grid place-items-center w-11 h-11 rounded-xl bg-primary/15 text-primary group-hover:bg-primary group-hover:text-black transition-colors">
                    <it.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/40">{it.label}</div>
                    <div className="text-sm font-medium mt-0.5">{it.value}</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Clock className="w-4 h-4 text-primary" /> {CONTACT.hours}
            </div>

            <div id="map" className="rounded-2xl overflow-hidden border border-white/10 h-64">
              <iframe
                title="Flyzone Location"
                data-testid="google-map"
                src="https://www.google.com/maps?q=FlyZone+Bike+And+Scooter+Rental+Service+Kothrud+Pune&ll=18.5104,73.8272&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(1) invert(0.9) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl p-7 bg-[#141414] border border-white/10 space-y-5 h-fit"
          >
            <div className="space-y-2">
              <Label className="text-white/80">Name</Label>
              <Input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="bg-[#0A0A0A] border-white/15" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/80">Email</Label>
                <Input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-[#0A0A0A] border-white/15" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">Phone</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="Optional" className="bg-[#0A0A0A] border-white/15" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/80">Message</Label>
              <Textarea data-testid="contact-message" required value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} className="bg-[#0A0A0A] border-white/15 resize-none" />
            </div>
            <Button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="w-full rounded-full bg-primary text-black hover:bg-primary/90 font-semibold py-6"
            >
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
